
import * as ReactDOM from 'react-dom';
import * as React from 'react';
import { DatasetAdapter, DatasetControl, Grid as GridBaseControl, IDatasetControlProps, IGrid } from "@talxis/base-controls";
import { IInputs, IOutputs } from '.';

const LICENSE_KEY = 'Using_this_{AG_Grid}_Enterprise_key_{AG-058326}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Microsoft_Corporation}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Business_Applications_Group}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Business_Applications_Group}_need_to_be_licensed___{Business_Applications_Group}_has_been_granted_a_Deployment_License_Add-on_for_{Unlimited}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{12_December_2025}____[v3]_[01]_MTc2NTQ5NzYwMDAwMA==9839d161bdceea4a5157119f5ce2bf89';

//Keys @talxis/base-controls' Grid component needs override for
const GRID_TRANSLATION_KEYS = [
    "filtermenu-filterby", "filtermenu-applybutton", "filtermenu-clearbutton",
    "filtersortmenu-sorttext-a-z", "filtersortmenu-sorttext-z-a",
    "filtersortmenu-sortdate-a-z", "filtersortmenu-sortdate-z-a",
    "filtersortmenu-sortnumber-a-z", "filtersortmenu-sortnumber-z-a",
    "filtersortmenu-sorttwooption-a-z", "filtersortmenu-sorttwooption-z-a", "filtersortmenu-sorttwooption-joint",
    "filtersortmenu-clearfilter", "filtersortmenu-clearsorting",
    "filtersortmenu-total-none", "filtersortmenu-total-avg", "filtersortmenu-total-max", "filtersortmenu-total-min",
    "filtersortmenu-total-sum", "filtersortmenu-total-count", "filtersortmenu-total-countcolumn", "filtersortmenu-total",
    "filtersortmenu-group", "filtersortmenu-ungroup",
    "norecordsfound",
    "saving-changenotification", "saving-clickreview", "saving-save", "saving-saving", "saving-changepreview-title",
    "saving-validation-error", "saving-save-all", "saving-discard-all", "saving-discard", "saving-discard-changes",
    "saving-save-changes", "saving-autosave-success", "saving-autosave-error", "saving-unsaved-changes",
    "saving-discard-all-confirmation",
    "validation-input-value", "validation-email", "validation-url", "validation-date", "validation-number",
    "no-name", "download", "value-not-editable", "original-value", "new-value", "maximumGroupChildrenLimitReached"
] as const;

//Keys @talxis/base-controls' DatasetControl component needs override for
const DATASET_CONTROL_TRANSLATION_KEYS = [
    "search", "records", "no-name", "column-source", "no-result-found", "add-column", "custom-column", "cancel",
    "edit-columns", "edit-filters", "save", "generalError", "reload", "unsaved-changes",
    "saving-discard-all-confirmation",
    "paging-of", "paging-firstpage", "paging-previouspage", "paging-page", "paging-nextpage", "paging-lastpage",
    "paging-pages", "paging-pages-totalcount", "page-record-count",
    "quickfind-like-warning", "quickfind-search-use-filter", "quickfind-search-starts-with", "quickfind-search-on-these-columns"
] as const;

export class Grid implements ComponentFramework.StandardControl<IInputs, IOutputs> {
    private _container: HTMLDivElement;
    private _adapter: DatasetAdapter = new DatasetAdapter();

    /**
     * Used to initialize the control instance.
     * @param context The entire property bag available to control via Context Object.
     * @param notifyOutputChanged A callback method to alert the framework that the control has new outputs.
     * @param state A piece of data that persists in one session for a single user.
     * @param container The div element within which the control can render its content.
     */
    public init(context: ComponentFramework.Context<IInputs, IOutputs>, notifyOutputChanged: () => void, state: ComponentFramework.Dictionary, container: HTMLDivElement): void {
        this._container = container;
        this._adapter.init(context, container, state);
    }

    //Builds the { key: { lcid: value } } shape useControlLabels expects. Only the Polish resx has these keys
    //today (translations.1029/1033.resx don't) - context.resources.getString() falls back to returning the
    //key itself when a resx entry is missing, so we must skip that case. Otherwise, for any language other
    //than Polish, we'd merge that raw key name over the package's own correct built-in text.
    private _buildTranslations(context: ComponentFramework.Context<IInputs, IOutputs>, keys: readonly string[]) {
        const result: { [key: string]: { [lcid: number]: string } } = {};
        for (const key of keys) {
            const value = context.resources.getString(key);
            if (value === key) {
                continue;
            }
            result[key] = { [context.userSettings.languageId]: value };
        }
        return result;
    }

    /**
     * Called when any value in the property bag has changed.
     * @param context The entire property bag available to control via Context Object.
     */
    public updateView(context: ComponentFramework.Context<IInputs, IOutputs>): void {
        return this._adapter.updateView(context, (datasetControlProps) => {
            ReactDOM.render(React.createElement(DatasetControl, {
                ...datasetControlProps,
                //IDatasetControlProps' declared translations type (functions) doesn't match useControlLabels' actual runtime merge contract (raw LCID maps)
                translations: this._buildTranslations(context, DATASET_CONTROL_TRANSLATION_KEYS) as unknown as IDatasetControlProps['translations'],
                onGetControlComponent: (props) => React.createElement(GridBaseControl, {
                    ...props,
                    parameters: {
                        ...props.parameters,
                        LicenseKey: {
                            raw: LICENSE_KEY
                        },
                        SelectableRows: {
                            raw: this._getSelectableRowsParameter(context.parameters.SelectableRows)
                        }
                    },
                    //IGrid's declared translations type (functions) doesn't match useControlLabels' actual runtime merge contract (raw LCID maps)
                    translations: this._buildTranslations(context, GRID_TRANSLATION_KEYS) as unknown as IGrid['translations']
                } as IGrid),
            } as IDatasetControlProps), this._container);
        });
    }

    /**
     * Called to retrieve the output values from the control.
     * @returns The output object containing the dataset control.
     */
    public getOutputs(): IOutputs {
        return {};
    }

    /**
     * Called when the control is to be removed from the DOM.
     */
    public destroy(): void {
        ReactDOM.unmountComponentAtNode(this._container);
        this._adapter.destroy();
    }

    private _getSelectableRowsParameter(selectableRows?: ComponentFramework.PropertyTypes.EnumProperty<"none" | "single" | "multiple">): "none" | "single" | "multiple" | null {
        switch (selectableRows?.raw) {
            //@ts-ignore - legacy binding support
            case 'true': {
                return 'multiple';
            }
            //@ts-ignore - legacy binding support
            case 'false': {
                return 'none';
            }
        }
        return selectableRows.raw ?? null;
    }
}